import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiClipboard, FiRepeat, FiTrendingUp,
  FiMessageSquare, FiLayers, FiArrowRight, FiStar, FiZap,
} from 'react-icons/fi';
import {
  fetchDwDashboard,
  fetchDwDashboardActivity,
  type DashboardRecentCommand,
  type DashboardRecentRun,
  type DashboardRecentChat,
  type DashboardAllActivity,
} from '../claudeagents/claudeAgentApi';
import { ROUTES } from '../../app/routes';
import { CHAT_ARCHIVED_EVENT } from '../../services/historyService';
import { resolveTaskTransactionId } from '../../services/kanbanAIService';
import { agentPhotoFor, useAgentPhotosVersion } from '../../utils/agentPhotos';
import { displayNameForAgent } from '../../utils/agentAvatar';
import '../../styles/DwAgentDashboardSidebar.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const KANBAN_STATUS_GUID: Record<string, string> = {
  '11111111-1111-1111-1111-111111111111': 'todo',
  '22222222-2222-2222-2222-222222222222': 'in_progress',
  '33333333-3333-3333-3333-333333333333': 'in_review',
  '44444444-4444-4444-4444-444444444444': 'done',
};

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function fmtDuration(sec?: number | null): string {
  if (!sec) return '';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function chatSessionTitle(title?: string | null): string {
  const trimmed = (title ?? '').trim();
  if (!trimmed || /^\[Runtime context\]/i.test(trimmed)) return 'Untitled conversation';
  return trimmed.split('\n')[0].trim() || 'Untitled conversation';
}

const STATUS_COLOR: Record<string, string> = {
  todo: '#6366f1', pending: '#6366f1', unknown: '#6366f1',
  in_progress: '#f59e0b', running: '#f59e0b',
  in_review: '#00b0d3',
  done: '#22c55e', completed: '#22c55e', success: '#22c55e',
  failed: '#ef4444', error: '#ef4444',
};
const STATUS_LABEL: Record<string, string> = {
  todo: 'To Do', pending: 'To Do', unknown: 'Unknown',
  in_progress: 'In Progress', running: 'Running',
  in_review: 'In Review',
  done: 'Done', completed: 'Done', success: 'Done',
  failed: 'Failed', error: 'Failed',
};

const AVATAR_COLORS = ['#8b5cf6', '#00b0d3', '#f59e0b', '#22c55e', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

type ActivityTab = 'activity' | 'commands' | 'runs' | 'chats';

type ActivityItem = {
  type: 'task' | 'run' | 'chat';
  title: string;
  status?: string;
  time: string;
  raw: string;
  navId?: string;
  agentId?: string;
  agentName?: string;
  cmdNumber?: string;
  duration?: string;
};

type TopAgent = {
  id: string; name: string; initials: string; color: string;
  successRate: number; runs: number; tasks: number; isDefault: boolean;
};

// ── Skeleton primitive ────────────────────────────────────────────────────────

const Skel: React.FC<{ w?: string | number; h?: number; r?: number; style?: React.CSSProperties }> = ({
  w = '100%', h = 10, r = 5, style,
}) => (
  <div className="dwa-sb-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

// ── Activity row (shared renderer) ───────────────────────────────────────────

const ActivityRow: React.FC<{ item: ActivityItem; onClick?: () => void }> = ({ item, onClick }) => {
  const statusKey = (item.status || '').toLowerCase().replace(/\s+/g, '_');
  const statusColor = STATUS_COLOR[statusKey] || '#64748b';
  const statusLabel = STATUS_LABEL[statusKey] || item.status || '';
  const typeColor = item.type === 'task' ? '#6366f1' : item.type === 'run' ? '#00b0d3' : '#8b5cf6';
  const icon = item.type === 'task'
    ? <FiClipboard size={11} />
    : item.type === 'run'
      ? <FiRepeat size={11} />
      : <FiMessageSquare size={11} />;

  return (
    <div
      className={`dwa-sb-activity__item${onClick ? ' dwa-sb-activity__item--clickable' : ''}`}
      onClick={onClick}
    >
      <div className="dwa-sb-activity__icon-wrap" style={{ background: `${typeColor}18`, color: typeColor }}>
        {icon}
      </div>
      <div className="dwa-sb-activity__text">
        <div className="dwa-sb-activity__title">
          {item.cmdNumber && (
            <span className="dwa-sb-activity__cmd-num">{item.cmdNumber}</span>
          )}
          <span className="dwa-sb-activity__title-text">{item.title}</span>
        </div>
        <div className="dwa-sb-activity__meta">
          {item.status && (
            <span className="dwa-sb-activity__status" style={{ color: statusColor }}>{statusLabel}</span>
          )}
          {item.duration && (
            <span className="dwa-sb-activity__duration">{item.duration}</span>
          )}
          {item.agentName && (
            <span className="dwa-sb-activity__agent">{item.agentName}</span>
          )}
          <span className="dwa-sb-activity__time">{item.time}</span>
        </div>
      </div>
      {onClick && <FiArrowRight size={10} className="dwa-sb-activity__arrow" />}
    </div>
  );
};

// ── Skeleton rows ─────────────────────────────────────────────────────────────

const SkeletonRows: React.FC = () => (
  <>
    {[72, 58, 80, 64, 70].map((w, i) => (
      <div className="dwa-sb-activity__item" key={i}>
        <Skel w={20} h={20} r={5} style={{ flexShrink: 0, marginTop: 1 }} />
        <div className="dwa-sb-skel-col">
          <Skel w={`${w}%`} h={10} r={4} />
          <div className="dwa-sb-skel-row">
            <Skel w={34} h={8} r={3} />
            <Skel w={26} h={8} r={3} />
          </div>
        </div>
      </div>
    ))}
  </>
);

// ── Component ─────────────────────────────────────────────────────────────────

interface DwAgentDashboardSidebarContentProps {
  /** Whether the right sidebar is open. The panel stays mounted while collapsed;
   *  its data loads the first time it is actually shown. */
  active?: boolean;
}

const DwAgentDashboardSidebarContent: React.FC<DwAgentDashboardSidebarContentProps> = ({ active = true }) => {
  useAgentPhotosVersion();
  const navigate = useNavigate();

  const [totalAgents, setTotalAgents]               = useState(0);
  const [premadeCount, setPremadeCount]             = useState(0);
  const [customCount, setCustomCount]               = useState(0);
  const [totalTasks, setTotalTasks]                 = useState(0);
  const [agentCount, setAgentCount]                 = useState(0);
  const [totalRuns, setTotalRuns]                   = useState(0);
  const [successRuns, setSuccessRuns]               = useState(0);
  const [failedRuns, setFailedRuns]                 = useState(0);
  const [totalChats, setTotalChats]                 = useState(0);
  const [overallSuccessRate, setOverallSuccessRate] = useState(0);
  const [recentCommands, setRecentCommands]         = useState<ActivityItem[]>([]);
  const [recentRuns, setRecentRuns]                 = useState<ActivityItem[]>([]);
  const [recentChats, setRecentChats]               = useState<ActivityItem[]>([]);
  const [topAgent, setTopAgent]                     = useState<TopAgent | null>(null);
  const [loading, setLoading]                       = useState(true);
  const [activityLoading, setActivityLoading]       = useState(false);
  const [activeTab, setActiveTab]                   = useState<ActivityTab>('activity');

  // An archived chat (sidebar, Chat History) leaves the activity lists right away.
  useEffect(() => {
    const onArchived = (e: Event) => {
      const id = (e as CustomEvent<{ conversationId: string }>).detail?.conversationId;
      if (id) setRecentChats((prev) => prev.filter((c) => c.navId !== id));
    };
    window.addEventListener(CHAT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(CHAT_ARCHIVED_EVENT, onArchived);
  }, []);

  // ── Helpers: map API responses → ActivityItem[] ─────────────────────────

  const mapCommands = useCallback((cmds: DashboardRecentCommand[]): ActivityItem[] =>
    cmds.map((t) => ({
      type: 'task' as const,
      title: t.title || 'Untitled command',
      status: KANBAN_STATUS_GUID[t.status_id || ''] || 'unknown',
      time: timeAgo(t.created_at),
      raw: t.created_at || '',
      navId: String(t.id),
      agentName: t.agent_name || undefined,
      cmdNumber: t.task_number || undefined,
    })), []);

  const mapRuns = useCallback((runs: DashboardRecentRun[]): ActivityItem[] =>
    runs.map((r) => ({
      type: 'run' as const,
      title: r.name || `Run #${r.id}`,
      status: r.status || undefined,
      time: timeAgo(r.started_at),
      raw: r.started_at || '',
      duration: fmtDuration(r.duration_sec),
      agentName: r.agent_name || undefined,
    })), []);

  const mapChats = useCallback((chats: DashboardRecentChat[]): ActivityItem[] =>
    chats.map((c) => ({
      type: 'chat' as const,
      title: chatSessionTitle(c.title),
      time: timeAgo(c.last_accessed || c.created),
      raw: c.last_accessed || c.created || '',
      navId: c.id,
      agentId: c.agent_id || undefined,
    })), []);

  // ── Stats fetch (on mount, no activity) ─────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const dashboard = await fetchDwDashboard().catch(() => null);
      if (!dashboard) { setLoading(false); return; }

      const { stats, top_agent } = dashboard;

      setTotalAgents(stats.total_agents);
      setPremadeCount(stats.premade_count);
      setCustomCount(stats.custom_count);
      setTotalTasks(stats.total_tasks);
      setAgentCount(stats.total_agents);
      setTotalRuns(stats.total_runs);
      setSuccessRuns(stats.success_runs);
      setFailedRuns(stats.failed_runs);
      setTotalChats(stats.total_chats);
      setOverallSuccessRate(stats.success_rate);

      if (top_agent) {
        setTopAgent({
          id: top_agent.agent_id,
          name: top_agent.name,
          initials: top_agent.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2),
          color: AVATAR_COLORS[0],
          successRate: top_agent.success_rate,
          runs: top_agent.runs,
          tasks: top_agent.tasks,
          isDefault: !!top_agent.is_system,
        });
      } else {
        setTopAgent(null);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  // ── Lazy activity fetch ──────────────────────────────────────────────────

  // Tracks whether the "all" call has already populated each tab's data.
  const allLoadedRef = useRef(false);
  const loadedTabsRef = useRef<Set<string>>(new Set());

  /** Fetch all 3 activity types in ONE call — used for the default "All" tab. */
  const fetchAllActivity = useCallback(async () => {
    if (allLoadedRef.current) return;
    const data: DashboardAllActivity = await fetchDwDashboardActivity('all');
    setRecentCommands(mapCommands(data.commands));
    setRecentRuns(mapRuns(data.runs));
    setRecentChats(mapChats(data.chats));
    allLoadedRef.current = true;
    loadedTabsRef.current.add('commands');
    loadedTabsRef.current.add('runs');
    loadedTabsRef.current.add('chats');
  }, [mapCommands, mapRuns, mapChats]);

  /** Fetch a single tab's data — only if not already loaded by the "all" call. */
  const fetchSingleTab = useCallback(async (tab: 'commands' | 'runs' | 'chats') => {
    if (loadedTabsRef.current.has(tab)) return;
    const data = await fetchDwDashboardActivity(tab);
    if (tab === 'commands') setRecentCommands(mapCommands(data.items as DashboardRecentCommand[]));
    else if (tab === 'runs') setRecentRuns(mapRuns(data.items as DashboardRecentRun[]));
    else if (tab === 'chats') setRecentChats(mapChats(data.items as DashboardRecentChat[]));
    loadedTabsRef.current.add(tab);
  }, [mapCommands, mapRuns, mapChats]);

  // Called when user clicks a tab.
  const handleTabClick = useCallback(async (tab: ActivityTab) => {
    setActiveTab(tab);
    setActivityLoading(true);
    try {
      if (tab === 'activity') {
        await fetchAllActivity();
      } else {
        await fetchSingleTab(tab as 'commands' | 'runs' | 'chats');
      }
    } catch { /* ignore */ }
    setActivityLoading(false);
  }, [fetchAllActivity, fetchSingleTab]);

  // Build the "All" merged view reactively from the individual tab data
  const allActivity = useMemo(() => {
    const all = [...recentCommands, ...recentRuns, ...recentChats];
    all.sort((a, b) => new Date(b.raw).getTime() - new Date(a.raw).getTime());
    return all.slice(0, 5);
  }, [recentCommands, recentRuns, recentChats]);

  // ── Mount: fetch stats, then auto-load the default "All" tab ─────────

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!active || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchStats().then(() => {
      // Auto-load the "All" tab (default selected) once stats are ready,
      // so the user sees activity immediately without needing to click.
      handleTabClick('activity');
    });
  }, [active, fetchStats, handleTabClick]);

  // ── Tabs config ───────────────────────────────────────────────────────────

  const tabs: { id: ActivityTab; label: string; icon: React.ReactNode; count: number; color: string }[] = [
    { id: 'activity', label: 'All',      icon: <FiZap size={11} />,         count: allActivity.length, color: '#8b5cf6' },
    { id: 'commands', label: 'Commands', icon: <FiClipboard size={11} />,   count: recentCommands.length, color: '#6366f1' },
    { id: 'runs',     label: 'Runs',     icon: <FiRepeat size={11} />,      count: recentRuns.length,     color: '#00b0d3' },
    { id: 'chats',    label: 'Chats',    icon: <FiMessageSquare size={11} />,count: recentChats.length,   color: '#8b5cf6' },
  ];

  const activeItems =
    activeTab === 'commands' ? recentCommands :
    activeTab === 'runs'     ? recentRuns     :
    activeTab === 'chats'    ? recentChats    :
    allActivity;

  // ── Quick actions ─────────────────────────────────────────────────────────

  const quickActions = [
    { label: 'Manage Digital Workforce', icon: <FiUsers size={13} />,     color: '#8b5cf6', cls: 'dwa-sb-action-btn--purple', iconBg: 'rgba(139,92,246,0.18)', route: ROUTES.DW_ARTIFACTS      },
    { label: 'Commands',      icon: <FiClipboard size={13} />, color: '#6366f1', cls: 'dwa-sb-action-btn--indigo', iconBg: 'rgba(99,102,241,0.18)',  route: ROUTES.MOMENTUM_COMMAND  },
    { label: 'Routines',      icon: <FiRepeat size={13} />,    color: '#00b0d3', cls: 'dwa-sb-action-btn--cyan',   iconBg: 'rgba(0,176,211,0.18)',   route: ROUTES.MOMENTUM_ROUTINES },
    { label: 'Artifacts',     icon: <FiLayers size={13} />,    color: '#f59e0b', cls: 'dwa-sb-action-btn--amber',  iconBg: 'rgba(245,158,11,0.18)',  route: ROUTES.ARTIFACTS         },
  ];

  // ── Stat cards ────────────────────────────────────────────────────────────

  const stats = loading ? [] : [
    {
      icon: <FiUsers size={15} />, iconBg: 'rgba(139,92,246,0.12)', iconColor: '#8b5cf6',
      val: totalAgents, lbl: 'Total Agents',
      footer: <span className="dwa-sb-footer-note--purple">{premadeCount} Default · {customCount} Custom</span>,
    },
    {
      icon: <FiClipboard size={15} />, iconBg: 'rgba(99,102,241,0.12)', iconColor: '#6366f1',
      val: totalTasks, lbl: 'Assigned Commands',
      footer: <span className="dwa-sb-footer-note--indigo">Across {agentCount} agent{agentCount !== 1 ? 's' : ''}</span>,
    },
    {
      icon: <FiRepeat size={15} />, iconBg: 'rgba(0,176,211,0.12)', iconColor: '#00b0d3',
      val: totalRuns, lbl: 'Routine Runs',
      footer: (
        <>
          <span className="dwa-sb-footer-note--green">{successRuns} Passed</span>
          <span className="dwa-sb-footer-sep">·</span>
          <span className="dwa-sb-footer-note--red">{failedRuns} Failed</span>
        </>
      ),
    },
    {
      icon: <FiTrendingUp size={15} />, iconBg: 'rgba(34,197,94,0.12)', iconColor: '#22c55e',
      val: `${overallSuccessRate}%`, lbl: 'Success Rate',
      footer: <span className="dwa-sb-footer-note--violet">{totalChats} Chat Sessions</span>,
    },
  ];

  return (
    <div className="dwa-sb-wrap">
      <div className="dwa-sb-body">

        {/* ── Stats 2×2 ── */}
        <div className="dwa-sb-stats">
          {loading ? (
            [0, 1, 2, 3].map(i => (
              <div className="dwa-sb-stat-card" key={i}>
                <div className="dwa-sb-stat-card__top">
                  <Skel w={28} h={28} r={8} />
                  <div className="dwa-sb-skel-col">
                    <Skel w="55%" h={14} r={4} />
                    <Skel w="78%" h={8} r={3} />
                  </div>
                </div>
                <div className="dwa-sb-stat-card__footer"><Skel w="65%" h={8} r={3} /></div>
              </div>
            ))
          ) : (
            stats.map((s, i) => (
              <div className="dwa-sb-stat-card" key={i}>
                <div className="dwa-sb-stat-card__top">
                  <div className="dwa-sb-stat-card__icon" style={{ background: s.iconBg, color: s.iconColor }}>
                    {s.icon}
                  </div>
                  <div className="dwa-sb-stat-card__body">
                    <span className="dwa-sb-stat-card__val">{s.val}</span>
                    <span className="dwa-sb-stat-card__lbl">{s.lbl}</span>
                  </div>
                </div>
                <div className="dwa-sb-stat-card__footer">{s.footer}</div>
              </div>
            ))
          )}
        </div>

        {/* ── Quick Actions ── */}
        <div className="dwa-sb-actions">
          <div className="dwa-sb-section-lbl">Quick Actions</div>
          <div className="dwa-sb-actions__grid">
            {quickActions.map((a, i) => (
              <button key={i} className={`dwa-sb-action-btn ${a.cls}`} onClick={() => navigate(a.route)}>
                <div className="dwa-sb-action-btn__icon" style={{ background: a.iconBg, color: a.color }}>{a.icon}</div>
                <span className="dwa-sb-action-btn__label">{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Top Agent ── */}
        <div className="dwa-sb-top-agent">
          <div className="dwa-sb-section-lbl">Top Agent</div>
          {loading ? (
            <div className="dwa-sb-top-agent__card">
              <div className="dwa-sb-top-agent__row">
                <Skel w={34} h={34} r={10} />
                <div className="dwa-sb-skel-col">
                  <Skel w="65%" h={11} r={4} />
                  <Skel w="42%" h={8} r={3} />
                </div>
                <Skel w={38} h={22} r={6} />
              </div>
              <div className="dwa-sb-top-agent__metrics">
                <Skel h={28} r={7} />
                <Skel h={28} r={7} />
              </div>
            </div>
          ) : topAgent ? (
            <div className="dwa-sb-top-agent__card">
              <div className="dwa-sb-top-agent__row">
                {(() => {
                  const photo = agentPhotoFor(topAgent.id, topAgent.isDefault ? displayNameForAgent(topAgent.name, true) : undefined);
                  return (
                    <div className="dwa-sb-top-agent__avatar" style={photo ? undefined : { background: topAgent.color }}>
                      {photo ? <img src={photo} alt="" /> : topAgent.initials}
                    </div>
                  );
                })()}
                <div className="dwa-sb-top-agent__info">
                  <div className="dwa-sb-top-agent__name">{topAgent.name}</div>
                  <div className="dwa-sb-top-agent__meta">
                    <FiStar size={9} style={{ color: '#f59e0b', flexShrink: 0 }} />
                    <span>{topAgent.isDefault ? 'Default' : 'Custom'}</span>
                  </div>
                </div>
                <div className={`dwa-sb-top-agent__rate ${
                  topAgent.successRate >= 80 ? 'dwa-sb-top-agent__rate--good'
                  : topAgent.successRate >= 50 ? 'dwa-sb-top-agent__rate--medium'
                  : 'dwa-sb-top-agent__rate--poor'
                }`}>{topAgent.successRate}%</div>
              </div>
              <div className="dwa-sb-top-agent__metrics">
                {[
                  { icon: <FiRepeat size={10} />,    val: topAgent.runs,  lbl: 'Runs', color: '#00b0d3', bg: 'rgba(0,176,211,0.1)',  border: 'rgba(0,176,211,0.2)'  },
                  { icon: <FiClipboard size={10} />, val: topAgent.tasks, lbl: 'Cmds', color: '#6366f1', bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.2)' },
                ].map((m, i) => (
                  <div key={i} className="dwa-sb-top-agent__metric" style={{ background: m.bg, border: `1px solid ${m.border}` }}>
                    <span style={{ color: m.color }}>{m.icon}</span>
                    <span className="dwa-sb-top-agent__metric-val">{m.val}</span>
                    <span className="dwa-sb-top-agent__metric-lbl">{m.lbl}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dwa-sb-empty">No agents found</div>
          )}
        </div>

        {/* ── Activity Tabs ── */}
        <div className="dwa-sb-activity">
          <div className="dwa-sb-section-lbl">Recent Activity</div>

          {/* Tab bar */}
          <div className="dwa-sb-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`dwa-sb-tab${activeTab === tab.id ? ' dwa-sb-tab--active' : ''}`}
                style={activeTab === tab.id ? { color: tab.color, borderBottomColor: tab.color } : {}}
                onClick={() => handleTabClick(tab.id)}
              >
                <span className="dwa-sb-tab__icon" style={activeTab === tab.id ? { color: tab.color } : {}}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="dwa-sb-tab-content">
            {(loading || activityLoading) ? (
              <SkeletonRows />
            ) : activeItems.length === 0 ? (
              <div className="dwa-sb-empty">No {activeTab === 'activity' ? 'activity' : activeTab} yet</div>
            ) : (
              activeItems.map((item, i) => (
                <ActivityRow
                  key={i}
                  item={item}
                  onClick={item.navId ? async () => {
                    if (item.type === 'task') {
                      const txId = await resolveTaskTransactionId(item.navId!);
                      navigate(txId
                        ? `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(txId)}`
                        : `${ROUTES.MOMENTUM_COMMAND}?taskId=${item.navId}`);
                    }
                    else if (item.type === 'run')
                      navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(item.navId!)}`);
                    else if (item.type === 'chat')
                      navigate(`${ROUTES.CHAT}/${item.navId}`, {
                        state: { dwAgentId: item.agentId, dwAgentName: item.agentName, dwSessionId: item.navId },
                      });
                  } : undefined}
                />
              ))
            )}
          </div>

          {/* View All button */}
          {!loading && activeItems.length > 0 && (
            <button
              className="dwa-sb-view-all-btn"
              onClick={() => navigate(ROUTES.DW_ARTIFACTS)}
            >
              View All in Agent Dashboard
              <FiArrowRight size={11} />
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default DwAgentDashboardSidebarContent;
